/**
 * Admin -> PAN verifier (customer-credentialed Protean OPV)
 *
 * Inspection proves a PAN is well-FORMED; only the Income Tax Department can
 * prove it was ISSUED, via Protean's Online PAN Verification. The customer
 * brings their OWN OPV entitlement (banks / exchanges / AIR-SFT filers
 * qualify); the credential is encrypted at rest and never returned.
 *
 * Two panels: connection config, and a live "verify a PAN" tool that shows the
 * format and registry claims SEPARATELY - so a user always knows which
 * question was answered.
 *
 * Backend: /v1/admin/pan-verifier (GET/PUT/test/DELETE) + POST /v1/discovery/verify/pan.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  FileSearch,
  Loader2,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  getPanVerifierConfig,
  upsertPanVerifierConfig,
  clearPanVerifierConfig,
  verifyPan,
  type PanVerifierAdminState,
  type VerifyPanResult,
} from '@/lib/api';

const INPUT =
  'glass-lite rounded-md px-3 py-2 text-sm w-full outline-none focus:border-primary/40 border border-border/40';

/** Protean's published status vocabulary, mapped to plain language + tone. */
const STATUS_COPY: Record<string, { label: string; tone: 'success' | 'warn' | 'danger' }> = {
  existing_valid: { label: 'Existing & valid', tone: 'success' },
  existing_valid_marked: { label: 'Existing & valid (marked with an event)', tone: 'warn' },
  not_found: { label: 'Record not found', tone: 'danger' },
  deleted: { label: 'Deleted', tone: 'danger' },
  deactivated: { label: 'Deactivated', tone: 'danger' },
  fake: { label: 'Fake', tone: 'danger' },
};

export function AdminPanVerifier(): JSX.Element {
  const [state, setState] = useState<PanVerifierAdminState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [kind, setKind] = useState<string>('none');
  const [enabled, setEnabled] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [userId, setUserId] = useState('');
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [hasStoredSecret, setHasStoredSecret] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [panInput, setPanInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyPanResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getPanVerifierConfig();
      setState(s);
      if (s.config) {
        setKind(s.config.kind);
        setEnabled(s.config.enabled);
        setHasStoredSecret(s.config.hasSecret);
        const cfg = s.config.config ?? {};
        setBaseUrl(typeof cfg['baseUrl'] === 'string' ? (cfg['baseUrl'] as string) : '');
        setUserId(typeof cfg['userId'] === 'string' ? (cfg['userId'] as string) : '');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load PAN verifier');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const entry = state?.catalogue.find((c) => c.kind === kind);
  const needsVendorFields = kind !== 'none';

  async function onSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const config: Record<string, unknown> = {};
      if (needsVendorFields) {
        config['baseUrl'] = baseUrl.trim();
        config['userId'] = userId.trim();
        if (secret.trim() && entry?.secretField) config[entry.secretField] = secret.trim();
      }
      await upsertPanVerifierConfig({ kind, config, enabled });
      setSecret('');
      setSaved(true);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function onClear() {
    setSaving(true);
    try {
      await clearPanVerifierConfig();
      setKind('none');
      setEnabled(false);
      setBaseUrl('');
      setUserId('');
      setSecret('');
      setHasStoredSecret(false);
      setVerifyResult(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear failed');
    } finally {
      setSaving(false);
    }
  }

  async function onVerify() {
    const pan = panInput.trim().toUpperCase();
    if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(pan)) {
      setVerifyResult({
        pan,
        format: {
          valid: false,
          explanation: 'Enter a 10-character PAN (five letters, four digits, one letter).',
        },
        registry: { configured: false },
      });
      return;
    }
    setVerifying(true);
    setVerifyResult(null);
    try {
      setVerifyResult(await verifyPan(pan));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verification failed');
    } finally {
      setVerifying(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="PAN verifier"
        subtitle="Confirm a PAN is real and active against your own Income-Tax-authorised verification entitlement."
        badges={[
          {
            label: state?.config?.enabled ? `Active: ${state.config.kind}` : 'Format checks only',
            variant: state?.config?.enabled ? 'success' : 'glass',
          },
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        }
      />

      {loading && <LoadingState caption="Loading PAN verifier..." />}
      {error && !loading && (
        <ErrorState title="PAN verifier" detail={error} onRetry={() => void load()} />
      )}

      {!loading && state && (
        <>
          <GlassCard className="p-4 mb-4">
            <CardHeader className="p-0 mb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" /> Two layers of certainty
              </CardTitle>
              <CardDescription className="text-xs">
                <strong>Format</strong> checks (entity-type letter, the never-issued 0000 block)
                prove a PAN is well-formed and always run. <strong>Registry</strong> verification
                proves it was actually issued and is active - that needs your own Protean Online PAN
                Verification credentials, which banks, exchanges and AIR/SFT filers are eligible for.
                The PAN you verify is sent only to the bureau and is never stored here.
              </CardDescription>
            </CardHeader>
          </GlassCard>

          {/* Live verify tool - works with format-only even before credentials are set. */}
          <GlassCard className="p-5 space-y-3 mb-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileSearch className="h-4 w-4 text-primary" /> Verify a PAN
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              <input
                className={`${INPUT} max-w-xs font-mono uppercase`}
                value={panInput}
                onChange={(e) => setPanInput(e.target.value)}
                placeholder="ABCPE1234F"
                maxLength={10}
                aria-label="PAN to verify"
              />
              <Button size="sm" onClick={() => void onVerify()} disabled={verifying}>
                {verifying ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSearch className="h-3 w-3" />}{' '}
                Verify
              </Button>
            </div>

            {verifyResult && (
              <div className="space-y-2">
                <div className="text-[11px] text-muted-foreground font-mono">{verifyResult.pan}</div>
                {/* Layer 1 - format */}
                <div
                  className={`glass-lite rounded-md p-3 text-xs border ${
                    verifyResult.format.valid ? 'border-success/40' : 'border-destructive/40'
                  }`}
                >
                  <div className="flex items-center gap-2 font-medium">
                    {verifyResult.format.valid ? (
                      <CheckCircle2 className="h-4 w-4 text-success" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    )}
                    Format {verifyResult.format.valid ? 'valid' : 'invalid'}
                  </div>
                  <div className="text-muted-foreground mt-1">{verifyResult.format.explanation}</div>
                </div>
                {/* Layer 2 - registry */}
                <RegistryPanel registry={verifyResult.registry} />
              </div>
            )}
          </GlassCard>

          {/* Connection config. */}
          <GlassCard className="p-5 space-y-4">
            <CardTitle className="text-sm">Verification service</CardTitle>
            <div>
              <label htmlFor="pv-kind" className="text-xs font-medium block mb-1">
                Provider
              </label>
              <select
                id="pv-kind"
                className={INPUT}
                value={kind}
                onChange={(e) => setKind(e.target.value)}
              >
                {state.catalogue.map((c) => (
                  <option key={c.kind} value={c.kind}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            {needsVendorFields && (
              <>
                <div>
                  <label htmlFor="pv-url" className="text-xs font-medium block mb-1">
                    API endpoint URL
                  </label>
                  <input
                    id="pv-url"
                    className={INPUT}
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="https://your-protean-endpoint/pan-verify"
                  />
                </div>
                <div>
                  <label htmlFor="pv-user" className="text-xs font-medium block mb-1">
                    User ID (your Protean TID login)
                  </label>
                  <input
                    id="pv-user"
                    className={INPUT}
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="your-opv-user-id"
                  />
                </div>
                <div>
                  <label htmlFor="pv-secret" className="text-xs font-medium block mb-1">
                    API key
                    {hasStoredSecret && (
                      <Badge variant="success" className="ml-2 text-[9px]">
                        Stored
                      </Badge>
                    )}
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="pv-secret"
                      className={INPUT}
                      type={showSecret ? 'text' : 'password'}
                      value={secret}
                      onChange={(e) => setSecret(e.target.value)}
                      placeholder={
                        hasStoredSecret ? 'Leave blank to keep the stored credential' : 'Paste the API key'
                      }
                    />
                    <Button
                      size="sm"
                      variant="glass"
                      type="button"
                      onClick={() => setShowSecret((v) => !v)}
                    >
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Encrypted at rest and never displayed again after saving.
                  </div>
                </div>
              </>
            )}

            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Enabled (verify calls will use this provider)
            </label>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button size="sm" onClick={() => void onSave()} disabled={saving}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
              </Button>
              {state.config && (
                <Button size="sm" variant="glass" onClick={() => void onClear()} disabled={saving}>
                  <Trash2 className="h-3 w-3" /> Clear
                </Button>
              )}
              {saved && (
                <span className="text-[11px] text-success flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Saved
                </span>
              )}
            </div>
          </GlassCard>
        </>
      )}
    </PageContainer>
  );
}

function RegistryPanel({
  registry,
}: {
  registry: VerifyPanResult['registry'];
}): JSX.Element {
  if (!registry.configured) {
    return (
      <div className="glass-lite rounded-md p-3 text-xs border border-border/40">
        <span className="text-muted-foreground">
          Registry verification not configured - format check only. Add your Protean OPV credentials
          below to confirm the PAN was actually issued.
        </span>
      </div>
    );
  }
  if (registry.ok === false) {
    return (
      <div className="glass-lite rounded-md p-3 text-xs border border-destructive/40">
        <div className="flex items-center gap-2 font-medium">
          <AlertCircle className="h-4 w-4 text-destructive" /> Registry check could not complete
        </div>
        <div className="text-destructive mt-1">{registry.error ?? 'unknown error'}</div>
      </div>
    );
  }
  const copy = registry.status ? STATUS_COPY[registry.status] : undefined;
  const border =
    copy?.tone === 'success'
      ? 'border-success/40'
      : copy?.tone === 'warn'
        ? 'border-amber-400/40'
        : 'border-destructive/40';
  return (
    <div className={`glass-lite rounded-md p-3 text-xs border ${border}`}>
      <div className="flex items-center gap-2 font-medium">
        {copy?.tone === 'success' ? (
          <CheckCircle2 className="h-4 w-4 text-success" />
        ) : (
          <AlertCircle className={`h-4 w-4 ${copy?.tone === 'warn' ? 'text-amber-400' : 'text-destructive'}`} />
        )}
        Registry: {copy?.label ?? registry.status ?? 'unknown'}
        {registry.elapsedMs != null && (
          <span className="text-muted-foreground font-normal">({registry.elapsedMs} ms)</span>
        )}
      </div>
      {registry.aadhaarSeeding && (
        <div className="text-muted-foreground mt-1">Aadhaar-PAN link: {registry.aadhaarSeeding}</div>
      )}
      {(registry.nameMatch != null || registry.dobMatch != null) && (
        <div className="text-muted-foreground mt-0.5">
          {registry.nameMatch != null && <>Name match: {registry.nameMatch ? 'yes' : 'no'} </>}
          {registry.dobMatch != null && <>· DOB match: {registry.dobMatch ? 'yes' : 'no'}</>}
        </div>
      )}
    </div>
  );
}
